import imageCompression from 'browser-image-compression';
import { supabase } from './supabase';
import { functionUrl } from './functions';

/**
 * Camada de storage — Cloudflare R2 + CDN próprio.
 *
 * Upload: o browser comprime a imagem para WebP e envia via Edge Function
 *         `upload-image`, que valida o JWT + ownership e faz o PUT no R2.
 * Read:   URLs públicas são geradas a partir de `VITE_CDN_URL` + path
 *         (`https://cdn.imoveisja.app.br/{path}`). Cache do Cloudflare na frente.
 * Delete: via Edge Function `delete-image` que valida o JWT + ownership.
 */

const CDN_URL = ((import.meta.env.VITE_CDN_URL as string | undefined) ?? '').replace(/\/$/, '');

// VITE_CDN_URL é obrigatório (R2 é o único storage). Não lançar no topo do
// módulo: storage.ts é importado de forma eager (PropertyDetail, MapPage,
// ImageUploader...) e um throw aqui derruba o SPA inteiro no boot. Avisamos
// em dev e deixamos as URLs quebrarem visivelmente caso falte.
if (!CDN_URL && import.meta.env.DEV) {
  console.warn(
    '[storage] VITE_CDN_URL não definido — leituras e uploads de imagem vão falhar. ' +
      'Defina VITE_CDN_URL (ex.: https://cdn.imoveisja.app.br) no .env.local.',
  );
}

/**
 * MIME types aceitos como upload. Inclui HEIC/HEIF do iOS e TIFF; o browser
 * pode falhar a decodificar HEIC/TIFF fora do Safari — nesse caso a etapa
 * de compressão lança e o erro chega no toast.
 */
export const ACCEPTED_IMAGE_MIME = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/tiff',
] as const;

/** Match defensivo: alguns navegadores reportam variantes (jpg, x-tiff, heic-sequence). */
function isAcceptedImage(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  if ((ACCEPTED_IMAGE_MIME as readonly string[]).includes(type)) return true;
  if (type === 'image/jpg') return true;
  if (type === 'image/x-tiff') return true;
  if (type.startsWith('image/heic') || type.startsWith('image/heif')) return true;
  return false;
}

export class UnsupportedImageTypeError extends Error {
  constructor(public readonly receivedType: string) {
    super('Formato de imagem não suportado. Use JPG, PNG, WebP, HEIC ou TIFF.');
    this.name = 'UnsupportedImageTypeError';
  }
}

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export class ImageTooLargeError extends Error {
  constructor(public readonly receivedBytes: number) {
    super(`Imagem muito grande. Limite de ${Math.floor(MAX_UPLOAD_BYTES / (1024 * 1024))}MB.`);
    this.name = 'ImageTooLargeError';
  }
}

/** Versão síncrona — útil quando o path já está em mãos e não há motivo para await. */
export function getPublicUrl(path: string): string {
  if (!path) return '';
  // Paths reais do R2 são relativos ({userId}/{file}.webp). URLs absolutas só
  // aparecem em dados de seed/teste local — devolve direto em vez de prefixar o CDN.
  if (/^https?:\/\//i.test(path)) return path;
  return `${CDN_URL}/${path.replace(/^\/+/, '')}`;
}

/**
 * Mantida `async` por compatibilidade com a API antiga (que retornava Promise).
 * Internamente é síncrono: monta a URL pública do CDN diretamente do path.
 */
export async function getSignedUrl(path: string): Promise<string> {
  return getPublicUrl(path);
}

export async function compressAndUpload(
  file: File,
  prefix: string,
  onProgress?: (pct: number) => void,
): Promise<string> {
  if (!isAcceptedImage(file)) {
    throw new UnsupportedImageTypeError(file.type || 'desconhecido');
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new ImageTooLargeError(file.size);
  }

  // Equilíbrio qualidade × storage:
  //   maxSizeMB 0.35  -> teto de ~350KB por foto (antes era 200KB; subimos
  //                      pra preservar nitidez em fotos com muito detalhe)
  //   initialQuality  -> 0.82 reduz banding/blocagem antes do loop de
  //                      compressão ajustar pra caber no maxSizeMB
  //   maxWidthOrHeight -> 1600 é o suficiente pra fotos em ficha de imóvel
  const compressed = await imageCompression(file, {
    maxSizeMB: 0.35,
    maxWidthOrHeight: 1600,
    initialQuality: 0.82,
    useWebWorker: true,
    fileType: 'image/webp',
    onProgress,
  });

  // Garante que o tipo do Blob seja image/webp (alguns navegadores deixam
  // o `type` vazio quando o resultado vem de canvas).
  const webp = compressed.type === 'image/webp'
    ? compressed
    : new File([compressed], 'image.webp', { type: 'image/webp' });

  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error('Sessão expirada. Faça login novamente.');

  const form = new FormData();
  form.append('file', webp);
  form.append('prefix', prefix);

  const res = await fetch(functionUrl('upload-image'), {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: form,
  });

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error(`Erro ao fazer upload: ${errBody.error ?? res.statusText}`);
  }

  const { path } = await res.json() as { path: string };
  return path;
}

export async function deleteStorageFile(path: string): Promise<void> {
  if (!path) return;

  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) return;

  const res = await fetch(functionUrl('delete-image'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ path }),
  });

  // Falhas de delete não bloqueiam o fluxo (ex.: usuário removendo foto
  // que já não existe no R2). Logamos para visibilidade.
  if (!res.ok) {
    console.warn('deleteStorageFile failed', res.status, await res.text().catch(() => ''));
  }
}
